import { toDisplayJson } from '@shared/utils';
import isDeepEqual from '@/lib/isDeepEqual.js';
import flattenWorkflowSteps from '@/lib/workflows/flattenWorkflowSteps.js';
import getWorkflowStepBranchGroups from '@/lib/workflows/getWorkflowStepBranchGroups.js';
import listWorkflowTestConfigFields from '@/lib/workflows/listWorkflowTestConfigFields.js';

const withoutKeys = (value, keys = []) => Object.fromEntries(Object.entries(value || {}).filter(([key]) => !keys.includes(key)));

// A past test's config, back into the Test window's field texts; null when it changed something that isn't a
// field of this step.
const resolveConfigTexts = ({ step, config }) => {
  const fields = listWorkflowTestConfigFields(step);
  const texts = {};

  for (const [key, value] of Object.entries(config || {})) {
    if (key !== 'options') {
      const field = fields.find((candidate) => candidate.kind === 'json' && candidate.target === key);

      if (!field) {
        return null;
      }

      texts[field.key] = toDisplayJson(value);
      continue;
    }

    const optionFields = fields.filter((field) => field.target.startsWith('options.'));
    const wholeField = fields.find((field) => field.target === 'options');
    const ownKeys = optionFields.map((field) => field.target.slice('options.'.length));

    for (const field of optionFields) {
      const optionKey = field.target.slice('options.'.length);

      if (value?.[optionKey] !== undefined && !isDeepEqual(value[optionKey], step.options?.[optionKey])) {
        texts[field.key] = field.kind === 'text' ? String(value[optionKey]) : toDisplayJson(value[optionKey]);
      }
    }

    const rest = withoutKeys(value, ownKeys);

    if (!isDeepEqual(rest, withoutKeys(step.options, ownKeys))) {
      if (!wholeField) {
        return null;
      }

      texts[wholeField.key] = toDisplayJson(rest);
    }
  }

  return texts;
};

// One step's past choice against the draft as it is now: the window's choice, or { dropped } with why it no longer
// fits. Records are matched as values (isDeepEqual), never as JSON text: a test read back from jsonb has its keys
// in jsonb's order, the source step's saved output in the order its code wrote them.
const resolveChoice = ({ step, request, candidates }) => {
  if (!step) {
    return { dropped: { reason: 'step' } };
  }

  const choice = {};

  if (request?.disabled === true) {
    if (['schedule', 'trigger', 'webhook'].includes(step.type)) {
      return { dropped: { reason: 'setting' } };
    }

    choice.disabled = true;
  }

  if (request?.config) {
    const texts = resolveConfigTexts({ step, config: request.config });

    if (!texts) {
      return { dropped: { reason: 'setting' } };
    }

    choice.config = texts;
  }

  if (request?.answer !== undefined) {
    const answers = step.type === 'branch' ? ['then', 'else'] : getWorkflowStepBranchGroups(step).map(({ key }) => key);

    if (!answers.includes(request.answer)) {
      return { dropped: { reason: 'branch' } };
    }

    choice.answer = request.answer;
  }

  if (request?.items !== undefined) {
    const items = Array.isArray(request.items) ? request.items : [];

    // With no records from the source step to pick from (it returned none, or the setup hasn't landed), the
    // test's own records are the list, so the person still sees and can narrow what it runs on.
    if (candidates.length === 0) {
      return { ...choice, items, records: items };
    }

    // A record the source step's latest output no longer holds stays in the test, listed after that output's and
    // marked, never dropped: the source step returning other records, or none, must not cost the person their test
    // (2026-10-02: once it returned none, a targeted retest was impossible).
    const notInLatest = items.filter((item) => !candidates.some((candidate) => isDeepEqual(candidate, item)));

    if (notInLatest.length > 0) {
      return { ...choice, items, records: [...candidates, ...notInLatest], notInLatest };
    }

    choice.items = items;
  }

  return choice;
};

/*
 * The workflow's stored test (`test.steps`, draft meta.test), back into Test window choices against the current
 * draft. A step whose choice no longer fits (the step is gone, a setting it changed isn't one the step has now, or
 * its branch was removed) is kept as { dropped }, never partly kept and never silently run as built: the window
 * names it and holds Run test until the person picks again or resets it. Picked records the source step's latest
 * output no longer holds still fit: they run as stored, listed as `notInLatest`.
 */
const resolveWorkflowTestChoices = ({ steps = {}, definitionSteps = [], loops = [] } = {}) => {
  const stepById = Object.fromEntries(flattenWorkflowSteps(definitionSteps).map((step) => [step.stepId, step]));
  const candidatesByLoopId = Object.fromEntries(loops.map(({ stepId, items }) => [stepId, Array.isArray(items) ? items : []]));

  return Object.fromEntries(Object.entries(steps || {}).map(([stepId, request]) => [stepId, resolveChoice({ step: stepById[stepId], request, candidates: candidatesByLoopId[stepId] || [] })]));
};

export default resolveWorkflowTestChoices;
